// ═══════════════════════════════════════════════════════════
// DepartmentCard — bilingual, Apple-inspired white theme
// ═══════════════════════════════════════════════════════════

import { motion } from "framer-motion";
import { Department, floorLabel } from "@/lib/data";
import { useLang } from "@/lib/i18n";

interface Props {
  dept: Department;
  index?: number;
  onClick: () => void;
  highlight?: string;
}

function highlightText(text: string, query: string): React.ReactNode {
  if (!query || !text) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-[rgba(74,144,217,0.18)] text-[#1A2747] rounded px-0.5">
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  );
}

export default function DepartmentCard({ dept, index = 0, onClick, highlight = "" }: Props) {
  const { isRtl } = useLang();
  const isEmpty = !dept.ar;
  const fl = floorLabel(dept.floor);

  // Choose primary/secondary label by language. Always show both for context.
  const primary = isRtl ? (isEmpty ? fl.ar : dept.ar) : (isEmpty ? fl.en : dept.en);
  const secondary = isRtl ? (isEmpty ? fl.en : dept.en) : (isEmpty ? fl.ar : dept.ar);
  const head = isRtl ? dept.headAr : (dept.headEn || dept.headAr);

  return (
    <motion.button
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22, delay: Math.min(index * 0.03, 0.4), ease: "easeOut" }}
      whileTap={{ scale: 0.985 }}
      onClick={onClick}
      style={{ touchAction: "manipulation" }}
      className={`
        w-full flex items-center gap-3 px-3.5 py-3 ${isRtl ? "text-right" : "text-left"} rounded-2xl border
        transition-all duration-200
        ${isEmpty
          ? "bg-[rgba(15,23,42,0.025)] border-[rgba(15,23,42,0.04)]"
          : "bg-white border-[rgba(15,23,42,0.06)] shadow-[0_1px_2px_rgba(15,23,42,0.03),0_6px_14px_rgba(15,23,42,0.04)] hover:shadow-[0_2px_4px_rgba(15,23,42,0.04),0_10px_22px_rgba(15,23,42,0.06)] hover:border-[rgba(74,144,217,0.20)]"
        }
      `}
    >
      {/* Icon */}
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-base flex-shrink-0
        ${isEmpty
          ? "bg-[rgba(15,23,42,0.05)] text-[#9CA3AF]"
          : "bg-[rgba(74,144,217,0.10)] text-[#1A2747]"
        }`}>
        {dept.icon}
      </div>

      {/* Body */}
      <div className="flex-1 min-w-0">
        <div className={`text-[13px] font-semibold leading-snug truncate ${isEmpty ? "text-[#9CA3AF]" : "text-[#1A2747]"}`}>
          {highlightText(primary, highlight)}
        </div>
        <div className={`text-[10px] mt-0.5 truncate font-light tracking-wide ${isEmpty ? "text-[#9CA3AF] opacity-70" : "text-[#6B7280]"}`}>
          {highlightText(secondary, highlight)}
        </div>
        {head && (
          <div className="text-[10px] text-[#6B7280] mt-1 truncate">
            {highlightText(head, highlight)}
          </div>
        )}
      </div>

      {/* Floor badge */}
      <div className="floor-badge flex-shrink-0">{dept.floor}</div>

      {/* Chevron — flips by direction */}
      <span className="text-[#9CA3AF] text-xs flex-shrink-0 mr-0.5">
        {isRtl ? "‹" : "›"}
      </span>
    </motion.button>
  );
}
